import React from 'react';
import { AlertTriangle, Info, MapPin, Navigation, Clock, Car, Ban, Hourglass, WifiOff } from 'lucide-react';
import { VISIBILITY_REASONS, getPrimaryReason } from '../../../../services/driverServiceVisibility';
import {
    VEHICLE_TYPE_LABELS,
    SERVICE_SEARCH_TIMEOUT_SEC,
    DRIVER_STATUSES,
    DEFAULT_SEARCH_RADIUS_KM
} from '../../../../constants';
import './DriverIncomingExplainer.css';

const REASON_LABELS = {
    [VISIBILITY_REASONS.NO_LOCATION]: 'ubicación',
    [VISIBILITY_REASONS.OUT_OF_RADIUS]: 'distancia',
    [VISIBILITY_REASONS.EXPIRED]: 'expiradas',
    [VISIBILITY_REASONS.VEHICLE_TYPE_MISMATCH]: 'tipo de vehículo',
    [VISIBILITY_REASONS.CATEGORY_DISABLED]: 'categoría desactivada'
};

const TIMEOUT_MINUTES = Math.round(SERVICE_SEARCH_TIMEOUT_SEC / 60);

const formatVehicleTypes = (types = []) => {
    const labels = types
        .filter(Boolean)
        .map(type => VEHICLE_TYPE_LABELS[type] || String(type).toUpperCase());
    if (labels.length === 0) return 'sin registrar';
    if (labels.length === 1) return labels[0];
    return `${labels.slice(0, -1).join(', ')} y ${labels[labels.length - 1]}`;
};

const formatKm = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
    return `${Number(value).toFixed(1)} km`;
};

const buildHiddenSummary = (diagnostics) => {
    const reasons = diagnostics?.reasons || {};
    return Object.keys(REASON_LABELS)
        .filter(reason => (reasons[reason] || 0) > 0)
        .map(reason => REASON_LABELS[reason])
        .join(', ');
};

const DriverIncomingExplainer = ({
    diagnostics = null,
    gpsOnline = false,
    driverStatus = null,
    onEnableGps = null,
    onRegisterVehicle = null
}) => {
    if (driverStatus && driverStatus !== DRIVER_STATUSES.ACTIVE) {
        const isPending = driverStatus === 'pending';
        return (
            <div className="bry-explainer bry-explainer--pending" role="status" aria-live="polite">
                <Hourglass size={18} aria-hidden="true" />
                <div className="bry-explainer__body">
                    <h3 className="bry-explainer__title">
                        {isPending
                            ? 'Tu cuenta está pendiente de aprobación'
                            : 'Tu cuenta de conductor no está activa'}
                    </h3>
                    <p className="bry-explainer__text">
                        {isPending
                            ? 'Te avisaremos cuando tu cuenta de conductor esté activa.'
                            : 'Contacta a administración desde la app para regularizar tu cuenta de conductor.'}
                    </p>
                </div>
            </div>
        );
    }

    if (!diagnostics) return null;

    const hiddenSummary = buildHiddenSummary(diagnostics);
    const hasVisible = (diagnostics.visible || 0) > 0;
    const primaryReason = getPrimaryReason(diagnostics);

    // Con solicitudes visibles solo mostramos una nota al pie: la tarjeta de
    // bloqueo solo tiene sentido cuando el conductor no ve ninguna.
    if (hasVisible) {
        if (!diagnostics.hidden) return null;
        return (
            <div className="bry-explainer bry-explainer--muted" role="status" aria-live="polite">
                <Info size={14} aria-hidden="true" />
                <p className="bry-explainer__text">
                    {diagnostics.hidden} solicitud{diagnostics.hidden === 1 ? '' : 'es'} no te mostrada
                    {diagnostics.hidden === 1 ? '' : 's'} por {hiddenSummary}.
                </p>
            </div>
        );
    }

    if (!primaryReason) return null;

    const radiusKm = diagnostics.radiusKm ?? DEFAULT_SEARCH_RADIUS_KM;
    const outOfRadiusKm = diagnostics.minOutOfRadiusKm ?? diagnostics.nearestKm;
    const driverTypes = formatVehicleTypes(diagnostics.activeVehicleTypes);
    const requestedTypes = formatVehicleTypes(diagnostics.vehicleTypesRequested);
    const disabledSlug = (diagnostics.disabledSlugs || [])[0];
    const disabledName = diagnostics.disabledCategories?.[disabledSlug] || disabledSlug;

    const CONTENT = {
        [VISIBILITY_REASONS.NO_LOCATION]: {
            icon: gpsOnline ? WifiOff : Navigation,
            title: gpsOnline
                ? 'Estamos esperando tu ubicación'
                : 'Activa la ubicación para recibir solicitudes',
            text: gpsOnline
                ? 'El GPS está encendido pero todavía no recibimos tu posición. Espera un momento o actívalo de nuevo.'
                : 'Enciende el GPS y recibirás las solicitudes que te quedan cerca.',
            action: !gpsOnline && onEnableGps ? { label: 'Activar GPS', onClick: onEnableGps } : null
        },
        [VISIBILITY_REASONS.OUT_OF_RADIUS]: {
            icon: MapPin,
            title: 'No hay solicitudes tan cerca',
            text: `El punto de recogida más cercano está a ${formatKm(outOfRadiusKm)} y el radio de búsqueda es de ${formatKm(radiusKm)}. Acércate o espera una solicitud en tu zona.`
        },
        [VISIBILITY_REASONS.EXPIRED]: {
            icon: Clock,
            title: `Las solicitudes duran ${TIMEOUT_MINUTES} minutos`,
            text: `La última solicitud venció mientras no había un conductor disponible. Espera a que un pasajero pida uno.`
        },
        [VISIBILITY_REASONS.VEHICLE_TYPE_MISMATCH]: {
            icon: Car,
            title: 'Tu vehículo no coincide con las solicitudes',
            text: `Tienes ${driverTypes} y las solicitudes disponibles son de ${requestedTypes}. Registra o activa otro vehículo para verlas.`,
            action: onRegisterVehicle ? { label: 'Registrar vehículo', onClick: onRegisterVehicle } : null
        },
        [VISIBILITY_REASONS.CATEGORY_DISABLED]: {
            icon: Ban,
            title: 'Tienes una categoría desactivada',
            text: `Desactivaste "${disabledName}", así que no te mostramos solicitudes de esa categoría. Reactívala en "Mis preferencias" de esta pantalla.`
        }
    };

    const content = CONTENT[primaryReason];
    if (!content) return null;
    const Icon = content.icon;

    return (
        <div className="bry-explainer bry-explainer--warning">
            <Icon size={18} aria-hidden="true" />
            <div className="bry-explainer__body">
                <div role="status" aria-live="polite">
                    <h3 className="bry-explainer__title">{content.title}</h3>
                    <p className="bry-explainer__text">{content.text}</p>
                </div>
                {content.action && (
                    <button
                        type="button"
                        className="bry-explainer__action"
                        onClick={content.action.onClick}
                    >
                        {content.action.label}
                    </button>
                )}
                {diagnostics.hidden > 0 && hiddenSummary && (
                    <p className="bry-explainer__footnote">
                        <AlertTriangle size={12} aria-hidden="true" />
                        No te mostradas por {hiddenSummary}.
                    </p>
                )}
            </div>
        </div>
    );
};

export default DriverIncomingExplainer;
